import { AppDialog } from "components/common/dialogs";
import React, { useState, useEffect } from "react";
import {
  Grid,
  Alert,
  Box,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/vi";
import { AppButton } from "components/common/buttons";
import { AppSelect, AppTextField } from "components/common/inputs";

const vatOptions = [
  { value: 0, label: "0%" },
  { value: 5, label: "5%" },
  { value: 8, label: "8%" },
  { value: 10, label: "10%" },
  { value: 15, label: "15%" },
  { value: 20, label: "20%" },
];

const PaymentProfileModal = ({
  open,
  onClose,
  onSave,
  contractId,
  contractNumber,
  contractName,
  editingProfile = null,
}) => {
  const [formData, setFormData] = useState({
    paymentName: "",
    paymentDate: dayjs().format("YYYY-MM-DD"),
    vat: 10,
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingProfile) {
      setFormData({
        paymentName: editingProfile.paymentName || "",
        paymentDate: editingProfile.paymentDate
          ? dayjs(editingProfile.paymentDate).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
        vat: Number(editingProfile.vat ?? 10),
        notes: editingProfile.notes || "",
      });
    } else {
      setFormData({
        paymentName: "",
        paymentDate: dayjs().format("YYYY-MM-DD"),
        vat: 10,
        notes: "",
      });
    }
    setErrors({});
  }, [editingProfile, open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.paymentName?.trim()) {
      newErrors.paymentName = "Tên kỳ thanh toán không được để trống";
    }

    if (!formData.paymentDate) {
      newErrors.paymentDate = "Ngày thanh toán không được để trống";
    }

    if ((formData.vat === "" || !Number.isInteger(Number(formData.vat)) || formData.vat < 0 || formData.vat > 100)) {
      newErrors.vat = "VAT phải từ 0% đến 100%";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const paymentProfileData = {
        contractId: contractId,
        paymentName: formData.paymentName.trim(),
        paymentDate: typeof formData.paymentDate === "string"
          ? formData.paymentDate
          : dayjs(formData.paymentDate).format("YYYY-MM-DD"),
        vat: Number(formData.vat),
        notes: formData.notes?.trim() || null,
      };

      await onSave(paymentProfileData, editingProfile?.paymentProfileId);
      onClose();
    } catch (error) {
      setErrors((current) => ({ ...current, submit: error?.response?.data?.message || error.message || "Không thể lưu kỳ thanh toán." }));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setFormData({
      paymentName: "",
      paymentDate: dayjs().format("YYYY-MM-DD"),
      vat: 10,
      notes: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      title={editingProfile ? "Chỉnh sửa kỳ thanh toán" : "Thêm kỳ thanh toán mới"}
      size="md"
      loading={loading}
      actions={
        <>
          <AppButton disabled={loading}
            onClick={handleClose}
            variant="outlined"
            color="inherit"
          >
            Hủy
          </AppButton>
          <AppButton
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? "Đang lưu..." : editingProfile ? "Cập nhật" : "Thêm mới"}
          </AppButton>
        </>
      }
    >
        {/* Contract Info */}
        <Box sx={{ mb: 3, p: 2, bgcolor: "#f8f9fa", borderRadius: 4 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Thông tin hợp đồng
          </Typography>
          <Typography variant="body2" sx={{ ml: 1 }}>
            <strong>Số hợp đồng:</strong> {contractNumber}
          </Typography>
          <Typography variant="body2" sx={{ ml: 1 }}>
            <strong>Tên hợp đồng:</strong> {contractName}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <AppTextField
              disabled={loading}
              fullWidth
              label="Tên kỳ thanh toán"
              value={formData.paymentName}
              onChange={(value) => handleChange("paymentName", value)}
              error={!!errors.paymentName}
              helperText={errors.paymentName}
              required
              placeholder="VD: Thanh toán lần 1, Tạm ứng, Nghiệm thu..."
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <AppTextField
              disabled={loading}
              fullWidth
              label="Ngày thanh toán"
              type="date"
              value={formData.paymentDate}
              onChange={(dateStr) => handleChange("paymentDate", dateStr)}
              error={!!errors.paymentDate}
              helperText={errors.paymentDate}
              required
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <AppSelect
              fullWidth
              label="VAT (%)"
              value={formData.vat}
              disabled={loading}
              options={vatOptions}
              onChange={(value) => handleChange("vat", value)}
              error={!!errors.vat}
              helperText={errors.vat}
              required
            />
          </Grid>

          <Grid size={12}>
            <AppTextField
              disabled={loading}
              fullWidth
              label="Ghi chú"
              value={formData.notes}
              onChange={(value) => handleChange("notes", value)}
              multiline
              minRows={3}
              placeholder="Ghi chú về kỳ thanh toán này..."
            />
          </Grid>
        </Grid>

        {Object.keys(errors).length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errors.submit || "Vui lòng kiểm tra lại thông tin đã nhập"}
          </Alert>
        )}
    </AppDialog>
  );
};

export default PaymentProfileModal;
